import axios from 'axios';
import axiosRetry from 'axios-retry';

import { ServerVersion, attemptReload } from './utils/version';

const APIBaseURLS = {
  development: process.env.API_BASE_URL,
  staging: process.env.API_BASE_URL || 'https://blade-api-staging.redmeru.com/api/',
  experimental: 'https://api.experimental.redmeru.com/api/',
  corpstaging: 'https://api.staging.corporate.redmeru.com/api/',
  aus: 'https://api.staging.aus.redmeru.com/api/',
  // TODO: Production backend
  production: process.env.API_BASE_URL || 'https://api.production.redmeru.com/api/'
};

const DashboardUrls = {
  development: 'http://localhost:8081',
  experimental: 'https://dashboards.experimental.redmeru.com',
  corpstaging: 'https://dashboards.staging.corporate.redmeru.com',
  aus: 'https://dashboards.staging.aus.redmeru.com',
  almas: 'https://dashboards.almas.bladeplatform.us',
  deloitte: 'https://dashboards.deloitte.bladeplatform.us'
};

const debugOverrideApiUrl = undefined;

let selectedEnvironment = (process.env.OVERRIDE_ENV || process.env.NODE_ENV);
if (selectedEnvironment === 'undefined') {
  selectedEnvironment = 'development';
}

// Workaround for local development environments
let workingBaseUrl = debugOverrideApiUrl
|| APIBaseURLS[process.env.OVERRIDE_ENV || process.env.NODE_ENV]
|| process.env.API_BASE_URL;

const isLocalhost = window.location.hostname === 'localhost';

// If the browser isn't accessing this from localhost, use the domain it's actually being accessed from.
if (workingBaseUrl.includes('localhost') && !isLocalhost) {
  workingBaseUrl = workingBaseUrl.replace('localhost', window.location.hostname);
}

export const APIBaseURL =
  workingBaseUrl
;


// Inconsistent because this is new.  The above should be change at some point.
export const DashboardURL = DashboardUrls[selectedEnvironment]
  || process.env.DASHBOARD_DEFAULT_URL;

console.log({ DashboardURL, selectedEnvironment });

export const axiosAPIInstance = axios.create({
  baseURL: APIBaseURL
});

const isNetworkError = error =>
  error && // just to make sure
  !error.response && // if there is a response, it reached the server and not a network error
  error.code !== 'ECONNABORTED'; // check that it isn't a timeout

const retryCondition = error =>
  isNetworkError(error) || // my custom check
  axiosRetry.isIdempotentRequestError(error); // retry lib check

axiosRetry(axiosAPIInstance, { retries: 3, retryDelay: axiosRetry.exponentialDelay, retryCondition });

let hasTokenExpired = false;
export const connectAPIInstanceToStore = (store) => {
  axiosAPIInstance.interceptors.request.use(
    (config) => {
      if (config.ignoreAdditionalHeaders) return config;
      config.headers['x-version'] = ServerVersion;

      const { authToken } = store.getters;
      // eslint-disable-next-line no-param-reassign
      if (authToken) config.headers['x-auth'] = authToken;
      return config;
    },
    err => Promise.reject(err)
  );

  axiosAPIInstance.interceptors.response.use(
    (response) => {
      const authToken = response.headers['x-auth'];
      if (authToken) store.dispatch('storeAuthToken', { authToken });
      return response;
    },
    (err) => {
      // err.response is undefined if no server response
      const { data, status } = err.response || {};
      if (status === 401 && data === 'Token Expired') {
        if (!hasTokenExpired) {
          // eslint-disable-next-line no-alert
          alert('Your session has expired.  Please log in again.');
          // refresh the page
          store.dispatch('logout');
          window.location.reload();

          // prevent spamming if we sent multiple requests
          hasTokenExpired = true;
        }
      }
      if (status === 400 && data === 'ServerVersionMismatch') {
        // eslint-disable-next-line no-alert
        attemptReload('Your client version is out of date');
        return;
      }
      return Promise.reject(err);
    }
  );
};

// ---- API path resolver (CloudFront fallback) -----------------------------------
// Chooses which API base URL this client can actually reach: the direct host first,
// then any configured fallback(s) (e.g. a CloudFront-proxied copy), right-to-left.
// The probe is a no-cors GET to <base with trailing / stripped>/ping -> /api/ping,
// a pure reachability beacon on the backend. The fetch RESOLVES on any HTTP response
// (opaque) and only REJECTS on network failure/timeout, so it is a clean IP-block
// signal and is immune to what the endpoint returns or to middleware.
const PROBE_TIMEOUT_MS = 5000; // 5s per probe (locked)

async function reachable(baseUrl) {
  const pingUrl = `${baseUrl.replace(/\/+$/, '')}/ping?probe=${Date.now()}`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    await fetch(pingUrl, { mode: 'no-cors', cache: 'no-store', signal: controller.signal });
    return true; // any HTTP response (opaque) = path OPEN
  } catch (e) {
    return false; // network error / abort = path BLOCKED or down
  } finally {
    clearTimeout(timer);
  }
}

async function resolveAPIPath() {
  // Anchor to the path the app already uses (APIBaseURL — derived from the
  // APIBaseURLS map / OVERRIDE_ENV and any localhost->host rewrite above). Tries the
  // direct host first, then any configured fallback(s) (e.g. a CloudFront-proxied
  // copy), right-to-left; the first OPEN path wins. Returns { url, layer } where
  // layer is 'primary' (the direct host), 'fallback' (a configured fallback), or
  // 'none' (nothing reachable).
  const direct = axiosAPIInstance.defaults.baseURL; // .../api/
  const fallbacks = (process.env.API_FALLBACK_BASE_URL || '')
    .split(',').map(s => s.trim()).filter(Boolean);
  const order = [direct, ...fallbacks.slice().reverse()]; // direct, then fallbacks right-to-left
  for (let i = 0; i < order.length; i++) {
    if (await reachable(order[i])) {
      return { url: order[i], layer: order[i] === direct ? 'primary' : 'fallback' };
    }
  }
  return { url: direct, layer: 'none' }; // none open -> keep the current url (identical to today)
}

// Health check — the second layer. A CORS GET to the ORIGIN root: the backend's `GET /`
// liveness route runs a real `User.findOne` and therefore pings the database. Returns
// 'ok' when it answers 2xx, else 'dbfail' (non-2xx, or the request itself failed/timed
// out). This is what lets us separate "reachable but the DB is down" from a plain
// network block, which the no-cors ping above cannot.
async function checkHealth(baseUrl) {
  let apiOrigin;
  try {
    apiOrigin = new URL(baseUrl).origin; // .../api/  ->  scheme://host[:port]
  } catch (e) {
    return 'ok'; // unparseable base: don't page, let normal boot surface any problem
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const res = await fetch(`${apiOrigin}/?healthcheck=${Date.now()}`, {
      mode: 'cors',
      cache: 'no-store',
      signal: controller.signal,
    });
    return res.ok ? 'ok' : 'dbfail'; // 2xx = healthy; anything else = DB (or server) trouble
  } catch (e) {
    return 'dbfail'; // reachable-for-ping but the health call failed -> treat as unhealthy
  } finally {
    clearTimeout(timer);
  }
}

function setAPIPath(url) {
  axiosAPIInstance.defaults.baseURL = url; // all real API calls go through this instance
  // v2: if socket is re-enabled, derive/point its server URL at the same resolved origin here.
}

export { resolveAPIPath, setAPIPath, checkHealth };



// WEBPACK FOOTER //
// ./src/axios-config.js